import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
    UrlTree
} from '@angular/router';
import { iif, Observable, of } from 'rxjs';
import { SessionQuery } from '../state/session/session.query';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';
import { UiService } from '../../shared/state/ui/ui.service';
import { SessionService } from '../state/session/session.service';
import { AppConfigQuery } from '../state/app-config/app-config.query';

@Injectable({
    providedIn: 'root'
})
export class SessionGuard implements CanActivate {
    constructor(
        private appConfig: AppConfigQuery,
        private session: SessionQuery,
        private router: Router,
        private ui: UiService,
        private sessionService: SessionService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        // keycloak guard is used, don't use legacy guard
        if (this.appConfig.get().useLatestKeycloakFlow) {
            return true;
        }

        this.ui.setUrl(state.url);
        const url = this.appConfig.get().landingPageOverride ?? state.url;
        this.ui.setUrl(url);
        if (this.session.isAuthenticated()) {
            return true;
        }

        if (!this.appConfig.get().bypassLogin) {
            // check if we're waiting for a new token...
            this.session.isLoading$.pipe(
                take(1),
                mergeMap((isLoading) =>
                    iif(
                        () => isLoading,
                        // ...wait until is loading returns false...
                        this.session.isLoading$.pipe(
                            filter((isLoading) => !isLoading),
                            // ...and check if user authenticated
                            map((_) => this.session.isAuthenticated())
                        ),
                        of(false)
                    )
                ),
                map((isAuthenticated) =>
                    isAuthenticated
                        ? true
                        : this.sessionService.logout('invalidAccessToken')
                )
            );
        }

        const refreshCookie = this.getRefreshCookie();
        if (!refreshCookie) {
            this.sessionService.logout('invalidRefreshToken');
            return false;
        }

        // ...try login with cookie value
        return this.sessionService.refreshTokenLogin(refreshCookie).pipe(
            // on error...
            catchError((err) => {
                this.sessionService.logout('invalidRefreshToken');
                return of(err);
            })
        );
    }

    getRefreshCookie() {
        let cookie: { [key: string]: string } = document.cookie
            .split(';')
            .reduce((prev, cur) => {
                const keyValue = cur.split('=');
                // Trim key since cookies are separated by "; " (semicolon space) if multiple
                return { ...prev, [keyValue[0].trim()]: keyValue[1] };
            }, {});
        return cookie?.swimbird || null;
    }
}
